import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { LocalStorageKeys } from '@app/services/local-storage.service';
import { Subscription } from 'rxjs';
import { debounce } from 'lodash';
import { appearDisappearCentered, appearDisappearNotCentered, appearDisappear } from './modal.utils';
import { DialogsStoreService } from '@app/services/dialogs/dialogs-store.service';

export enum GrabPlaces {
  header,
  none,
  top,
  right,
  bottom,
  left,
  topLeft,
  topRight,
  bottomLeft,
  bottomRight,
}

export interface ModalSizes {
  top: number;
  left: number;
  width: number;
  height: number;
}

@Component({
  standalone: false,
  selector: 'modal',
  templateUrl: './modal.component.html',
  styleUrls: ['./modal.component.scss'],
  encapsulation: ViewEncapsulation.None,
  animations: [appearDisappear, appearDisappearCentered, appearDisappearNotCentered],
})
export class ModalComponent implements OnInit, OnChanges, OnDestroy, AfterViewInit {
  readonly grabPlaces = GrabPlaces;

  constructor(
    private readonly renderer: Renderer2,
    private readonly ngZone: NgZone,
    private readonly cd: ChangeDetectorRef,
    private readonly dialogsStoreService: DialogsStoreService,
    public readonly elementRef: ElementRef,
  ) {}

  private grabTop;
  private grabLeft;
  private grabPlace = GrabPlaces.none;
  private subs = new Subscription();

  isGrabbed = false;
  isResized = false;
  isPinned = false;
  // isCentered = true;
  show = true;
  modal: ClientRect;
  zIndex: number;

  visibility: 'visible' | 'hidden' = 'visible';

  private loadedModal = false;
  private oldWidth: number;
  private oldHeight: number;

  public _top: number;
  get top() {
    return this._top;
  }
  @Input() set top(value) {
    value = typeof value === 'string' ? this.getSizeFromString(value, window.innerWidth) : value;

    const maxValue: number = window.innerHeight - 20;
    this._top = value < 0 ? 0 : value > maxValue ? maxValue : value;
  }

  public _left: number;
  get left() {
    return this._left;
  }
  @Input() set left(value) {
    value = typeof value === 'string' ? this.getSizeFromString(value, window.innerWidth) : value;

    const maxValue: number = window.innerWidth + (+this._width - 20);
    const minValue: number = 0 - (+this._width - 20);

    this._left = value < minValue ? minValue : value > maxValue ? maxValue : value;
  }

  public _width: number;
  @Input() set width(value: number | string) {
    this.setSize(value, 'width');
  }
  get width(): number {
    return this._width;
  }

  public _height: number;
  @Input() set height(value: number | string) {
    this.setSize(value, 'height');
  }
  get height(): number {
    return this._height;
  }

  @Input() name: LocalStorageKeys;
  @Input() resizable: boolean = false;
  @Input() isDraggable: boolean = true;
  @Input() fullScreen: boolean = false;
  @Input() showBackgroundMask = true;
  @Input() isPinnable = false;
  @Input() minWidth = 200;
  @Input() minHeight = 200;
  @Input() maxWidth: number;
  @Input() maxHeight: number;
  @Input() closeOnClickOutside = false;
  @Input() anchorElement: HTMLElement;
  @Input() ignoreEscKey = false;
  @Input() closeWithNoHide = false;
  @Input() keepModalOpenOnClose = false;

  @Input() noCloseOutside = false;
  @Output() closeByModal = new EventEmitter();

  @Output() closeOut = new EventEmitter();

  // these three events are added to be consistent with PopupComponent
  @Output() positionChange = new EventEmitter<any>();
  @Output() open = new EventEmitter<any>();
  @Output() close = new EventEmitter<any>();

  @ViewChild('element') element: ElementRef;

  @HostListener('window:resize', ['$event']) handleWindowResize = debounce((event) => {
    if (this.anchorElement) {
      this.locateModalToAnchor();
    }
  }, 70);

  handleClickOutside() {
    if (this.closeOnClickOutside) {
      this.doClose();
    }
  }

  ngAfterViewInit() {
    if (this.anchorElement) {
      this.locateModalToAnchor();
      return;
    }

    if (!this.top && !this.left) {
      this.locateModalToCenter();
      this.cd.detectChanges();
    }

    this.loadedModal = true;

    this.onPositionChange();
  }

  ngOnInit(): void {
    const val = this.dialogsStoreService.add({
      dialog: this,
      elementRef: this.elementRef,
      ignoreEscKey: this.ignoreEscKey,
      close: () => this.doClose(),
    });
    this.zIndex = val.zIndex;

    if (this.showBackgroundMask) {
      this.addMask();
    }

    if (this.resizable && this.name && localStorage.getItem(this.name)) {
      this.loadStyles();
      this.isPinned = true;
    }

    setTimeout(() => this.open.emit({}));

    // if (this.anchorElement) {
    //   this.isCentered = false;
    // }
  }

  loadStyles() {
    const sizes: ModalSizes = JSON.parse(localStorage.getItem(this.name));

    this._top = sizes.top; // priority higher than outside size
    this._left = sizes.left; // priority higher than outside size
    this._width = sizes.width; // priority higher than outside size
    this._height = sizes.height; // priority higher than outside size
  }

  ngOnChanges(changes) {
    if (this.anchorElement) {
      this.locateModalToAnchor();
      return;
    }

    if ((changes.top && this.top) || (changes.left && this.left)) {
      this.top = this.top || this.element.nativeElement.getBoundingClientRect().top;
      this.left = this.left || this.element.nativeElement.getBoundingClientRect().left;
      // this.isCentered = false;
    }
  }

  handleHeaderMousedown(event) {
    event.stopPropagation();

    if (this.fullScreen || !this.isDraggable) {
      return;
    }

    this.isGrabbed = true;
    this.grabPlace = GrabPlaces.header;
    this.grab(event);
  }

  enableResize(event, grabPlace: GrabPlaces) {
    this.isResized = true;
    this.grabPlace = grabPlace;
    this.grab(event);
  }

  grab(event) {
    this.modal = this.element.nativeElement.getBoundingClientRect();
    this.grabTop = event.pageY;
    this.grabLeft = event.pageX;
    this.top = this.modal.top;
    this.left = this.modal.left;
    // this.isCentered = false;
    this.cd.detectChanges();

    this.ngZone.runOutsideAngular(() => {
      const removeMousemoveListener = this.renderer.listen('window', 'mousemove', (mousemoveEvent) => {
        this.modal = this.element.nativeElement.getBoundingClientRect();

        mousemoveEvent.preventDefault();

        if (
          mousemoveEvent.pageX <= 0 ||
          mousemoveEvent.pageY <= 0 ||
          mousemoveEvent.pageX >= window.innerWidth ||
          mousemoveEvent.pageY >= window.innerHeight
        ) {
          removeMousemoveListener();
          this.drop();
          return;
        }

        switch (this.grabPlace) {
          case GrabPlaces.none:
            break;

          case GrabPlaces.header:
            this.top = this.modal.top + mousemoveEvent.pageY - this.grabTop;
            this.left = this.modal.left + mousemoveEvent.pageX - this.grabLeft;
            break;

          case GrabPlaces.top:
            this.top = this.modal.top + mousemoveEvent.pageY - this.grabTop;
            this.height = this.modal.height + this.grabTop - mousemoveEvent.pageY;
            break;

          case GrabPlaces.right:
            this.width = this.modal.width + mousemoveEvent.pageX - this.grabLeft;
            break;

          case GrabPlaces.bottom:
            this.height = this.modal.height + mousemoveEvent.pageY - this.grabTop;
            break;

          case GrabPlaces.left:
            this.left = this.modal.left + mousemoveEvent.pageX - this.grabLeft;
            this.width = this.modal.width + this.grabLeft - mousemoveEvent.pageX;
            break;

          case GrabPlaces.topLeft:
            this.top = this.modal.top + mousemoveEvent.pageY - this.grabTop;
            this.height = this.modal.height + this.grabTop - mousemoveEvent.pageY;
            this.width = this.modal.width + this.grabLeft - mousemoveEvent.pageX;
            this.left = this.modal.left + mousemoveEvent.pageX - this.grabLeft;
            break;

          case GrabPlaces.topRight:
            this.top = this.modal.top + mousemoveEvent.pageY - this.grabTop;
            this.height = this.modal.height + this.grabTop - mousemoveEvent.pageY;
            this.width = this.modal.width + mousemoveEvent.pageX - this.grabLeft;
            break;

          case GrabPlaces.bottomLeft:
            this.height = this.modal.height + mousemoveEvent.pageY - this.grabTop;
            this.width = this.modal.width + this.grabLeft - mousemoveEvent.pageX;
            this.left = this.modal.left + mousemoveEvent.pageX - this.grabLeft;
            break;

          case GrabPlaces.bottomRight:
            this.width = this.modal.width + mousemoveEvent.pageX - this.grabLeft;
            this.height = this.modal.height + mousemoveEvent.pageY - this.grabTop;
            break;
        }

        this.cd.detectChanges();
        this.grabTop = mousemoveEvent.pageY;
        this.grabLeft = mousemoveEvent.pageX;
      });

      const removeMouseupListener = this.renderer.listen('window', 'mouseup', (mouseupEvent) => {
        removeMouseupListener();
        removeMousemoveListener();
        this.drop();
        this.cd.detectChanges();
      });
    });
  }

  drop() {
    this.isGrabbed = false;
    this.isResized = false;
    this.grabPlace = GrabPlaces.none;
    if (this.isPinned) {
      this.save();
    }

    this.onPositionChange();
  }

  addMask() {
    this.showBackgroundMask = true;
  }

  removeMask() {
    this.showBackgroundMask = false;
  }

  doClose() {
    if (this.noCloseOutside) {
      this.closeByModal.emit();
      return;
    }
    if (!this.closeWithNoHide && !this.keepModalOpenOnClose) {
      this.show = false;
    }

    if (!this.keepModalOpenOnClose) {
      this.visibility = 'hidden';
    }

    this.closeOut.emit();
    this.close.emit();
    this.removeMask();
  }

  hide() {
    this.show = false;
    this.visibility = 'hidden';
    this.removeMask();
  }

  ngOnDestroy(): void {
    if (this.showBackgroundMask) {
      this.removeMask();
    }
    this.subs.unsubscribe();
  }

  save() {
    const sizes: ModalSizes = { top: this.top, left: this.left, width: this.width, height: this.height };
    localStorage.setItem(this.name, JSON.stringify(sizes));
  }

  // unsave() {
  //   localStorage.removeItem(this.name);
  // }

  // togglePin() {
  //   if (this.isPinned) {
  //     this.unsave();
  //     this.isPinned = false;
  //   } else {
  //     this.save();
  //     this.isPinned = true;
  //   }
  // }

  locateModalToCenter() {
    const top = this.top;
    const left = this.left;

    this.top = 0;
    this.left = 0;
    this.cd.detectChanges();

    const width =
      this.maxWidth && this.width >= this.maxWidth ? this.maxWidth : this.width || this.getComputedSize('width');
    const height =
      this.maxHeight && this.height >= this.maxHeight ? this.maxHeight : this.height || this.getComputedSize('height');

    this.top = top;
    this.left = left;

    if (!height || !width) {
      return;
    }

    this.cd.detectChanges();
    this.top = window.innerHeight / 2 - height / 2;
    this.left = window.innerWidth / 2 - width / 2;

    this.oldWidth = width;
    this.oldHeight = height;
  }

  locateModalToAnchor() {
    const anchor = this.anchorElement.getBoundingClientRect();
    const margin = 10;

    this.top = margin + anchor.top + anchor.height;
    this.left = anchor.left;
  }

  private locateWindowRelativeToCenter(type: 'width' | 'height', oldValue: number, newValue: number) {
    const difference = newValue - oldValue;

    if (type === 'width') {
      this.left -= difference / 2;
    } else {
      this.top -= difference / 2;
    }
  }

  private getSizeFromString(value: string, viewportSize: number): number {
    return value.match('%') ? viewportSize * +value.match(/\d+/)[0] * 0.01 : +value;
  }

  private setSize(value: string | number, type: 'width' | 'height') {
    const viewportSize: number = type === 'width' ? window.innerWidth : window.innerHeight;

    if (this.isResized && typeof value === 'number') {
      if (type === 'width') {
        this.oldWidth = value;
        this._width = value;
      } else {
        this.oldHeight = value;
        this._height = value;
      }
      return;
    }

    if (type === 'width') {
      this.oldWidth = this.width || this.getComputedSize(type);
    } else {
      this.oldHeight = this.height || this.getComputedSize(type);
    }

    if (!this.loadedModal) {
      value = typeof value === 'string' ? this.getSizeFromString(value, viewportSize) : value;

      if (type === 'width') {
        this.oldWidth = value || null;
        this._width = value || null;
      } else {
        this.oldHeight = value || null;
        this._height = value || null;
      }
      return;
    } else if (!value || (typeof value === 'string' && value === 'auto')) {
      if (type === 'width') {
        this._width = null;
      } else {
        this._height = null;
      }
      this.visibility = 'hidden';
      return;
    }

    value = typeof value === 'string' ? this.getSizeFromString(value, viewportSize) : value;

    if (type === 'width' && this.left) {
      this.locateWindowRelativeToCenter(type, this.oldWidth, value);
    } else if (type === 'height' && this.top) {
      this.locateWindowRelativeToCenter(type, this.oldHeight, value);
    }

    if (type === 'width') {
      this.oldWidth = value;
      this._width = value;
    } else {
      this.oldHeight = value;
      this._height = value;
    }
  }

  private getComputedSize(type: 'width' | 'height'): number {
    return this.element && this.element.nativeElement
      ? type === 'height'
        ? +getComputedStyle(this.element.nativeElement).height.slice(0, -2)
        : +getComputedStyle(this.element.nativeElement).width.slice(0, -2)
      : null;
  }

  onResize() {
    if (this.resizable) {
      this.save();
    }
    const newWidth = this.getComputedSize('width');
    const newHeight = this.getComputedSize('height');

    if (!this.isResized) {
      if (this.oldHeight && newHeight && this.oldHeight !== newHeight) {
        this.locateWindowRelativeToCenter('height', this.oldHeight, newHeight);
        this.visibility = 'visible';
        this.cd.detectChanges();
      }

      if (this.oldWidth && newWidth && this.oldWidth !== newWidth) {
        this.locateWindowRelativeToCenter('width', this.oldWidth, newWidth);
        this.visibility = 'visible';
        this.cd.detectChanges();
      }
    }

    this.oldHeight = newHeight;
    this.oldWidth = newWidth;

    if (this.fullScreen) {
      this.left = 0;
      this.top = 0;
      this.cd.detectChanges();
    }

    this.onPositionChange();
  }

  private onPositionChange() {
    this.positionChange.emit({});
  }

  minimize() {
    this.visibility = 'hidden';
    //this.cd.detectChanges();
  }

  restore() {
    this.visibility = 'visible';
    //this.cd.detectChanges();
  }
}
